import React, { useState } from "react";
import ErrorRow from "Common/ErrorRow";
import BreadCrumb from "Common/BreadCrumb";
import { MessageSquare, Search, RefreshCw } from "lucide-react";
import { ToastContainer } from "react-toastify";
import useAdminConversations from "hooks/useAdminConversations";
import { AdminConversation } from "services/conversation.service";

const Conversations = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const { data: response, isLoading, isError, refetch } = useAdminConversations({
    page,
    limit: 30,
  });

  const conversations: AdminConversation[] = response?.data?.data ?? [];
  const total = response?.data?.total ?? 0;
  const totalPages = Math.ceil(total / 30);

  const filtered = search
    ? conversations.filter((c) =>
        c.participants.some((p) =>
          p.name.toLowerCase().includes(search.toLowerCase())
        )
      )
    : conversations;

  return (
    <React.Fragment>
      <BreadCrumb title="Conversations" pageTitle="Messaging" />
      <ToastContainer closeButton={false} limit={1} />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-2.5 size-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter this page by participant name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            />
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
          <span className="sm:ml-auto text-sm text-gray-500">{total} total</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Participants</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Last Message</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-16">Msgs</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Last Active</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <ErrorRow colSpan={4} label="Couldn't load conversations" onRetry={refetch} />
              ) : isLoading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-16 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <MessageSquare className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No conversations found</p>
                  </td>
                </tr>
              ) : (
                filtered.map((conv) => (
                  <tr key={conv._id} className="hover:bg-gray-50 transition-colors">
                    <td data-label="Participants" data-cell="title" className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        {conv.participants.map((p, i) => (
                          <span key={i} className="text-gray-800 font-medium text-sm">
                            {p.name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td data-label="Last Message" className="px-4 py-3 text-gray-500 sm:max-w-xs">
                      {conv.latestMessage ? (
                        <p className="truncate text-sm">{conv.latestMessage.body || "—"}</p>
                      ) : (
                        <span className="text-gray-300 text-xs italic">No messages yet</span>
                      )}
                    </td>
                    <td data-label="Msgs" className="px-4 py-3 text-gray-600 text-center">
                      {conv.messageCount ?? 0}
                    </td>
                    <td data-label="Last Active" className="px-4 py-3 text-gray-500 whitespace-nowrap text-sm">
                      {new Date(conv.updatedAt).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Conversations;
